import React from 'react'
import { IProps, Mode } from './index'
import { IHeroImage, IVideo } from '../HeroWithCTA/index'

import { RichText } from '../../elements/RichText'
import { Text } from '../../elements/Text'
import { ImageOrAutoplayVideo } from '../../elements/ImageOrAutoplayVideo'
import { mq, fontGraphik } from '../../utils'
import { Hero } from '../Hero'
import { Image } from '../../elements/Image'
import classnames from 'classnames'

export function HeroForCareersPage({
  eyebrow,
  headline,
  subheading,
  footnote,
  layout,
  media,
  renderCta,
  mode = Mode.Normal,
  withGradient = false,
}: IProps) {
  // Type Guards
  function isImage(data: IHeroImage | IVideo | undefined): data is IHeroImage {
    return !!(data as IHeroImage)?.imageRef
  }

  function isVideo(data: IHeroImage | IVideo | undefined): data is IVideo {
    return !!(data as IVideo)?.video
  }

  function renderCareersPageContent() {
    return (
      <div className="cta-wrapper">
        <div className="cta">{renderCta && renderCta()}</div>
        {footnote && (
          <div className="footnote">
            <Text size="caption">
              <RichText html={footnote} />
            </Text>
          </div>
        )}
        <style jsx>
          {`
            .cta-wrapper {
              display: grid;
              grid-gap: 1rem;
            }
            .footnote {
              display: block;
            }
            img {
              max-width: 100%;
              margin-top: 3rem;
            }
          `}
        </style>
      </div>
    )
  }

  function renderImage() {
    return (
      isImage(media) && (
        <>
          <Image altText={headline} {...media.imageRef} />
          <style jsx>
            {`
              img {
                max-width: 100%;
              }
            `}
          </style>
        </>
      )
    )
  }

  function renderVideo() {
    return (
      isVideo(media) && (
        <ImageOrAutoplayVideo
          video={media.videosWithBreakpoints || media.video}
          videoWebm={media.videoWebm}
          imageRef={{
            url: media.fallbackImage,
            altText: headline,
          }}
          playOnce={media.playOnce}
        />
      )
    )
  }

  const determineMediaIsImageOrVideo =
    media &&
    (isImage(media)
      ? { isBackground: media.isBackground, renderAuxContent: renderImage }
      : {
          isBackground: media.isBackground,
          renderAuxContent: renderVideo,
        })

  const mobileImage = isImage(media) && media.mobileImage && media.mobileImage.url

  const heroHasContent = eyebrow || headline || subheading

  const careersHeroClasses = classnames('careers-hero-wrapper', {
    'careers-hero-wrapper--compact': mode === Mode.Compact,
    'careers-hero-wrapper--with-gradient': media && withGradient,
    'careers-hero-wrapper--has-content': heroHasContent,
  })

  return (
    <>
      <div className={careersHeroClasses}>
        <Hero
          eyebrow={eyebrow}
          headline={headline}
          subheading={subheading}
          renderMainContent={renderCareersPageContent}
          layout={layout}
          media={determineMediaIsImageOrVideo}
        />
      </div>
      <style jsx global>
        {`
          .careers-hero-wrapper {
            .headline span {
              font-family: 'Intercom Brush Small', ${fontGraphik};
              font-weight: normal;
              font-size: 4.0625rem;
            }
            .one-column .hero {
              grid-row-gap: 5rem;
            }
            .two-columns {
              &.hero-wrapper {
                background-image: url(${mobileImage});
                background-repeat: no-repeat;
                background-position: center;
                background-size: contain;
                height: 570px;
              }
              .content {
                position: absolute;
                top: 11.25rem;
                bottom: 0;
                grid-gap: 0;
              }
              .background-aux-content {
                grid-column: left-content / span 3;
                position: relative;
                display: block;
                picture {
                  visibility: hidden;
                }
                :global(.video-player-wrapper) {
                  border-radius: 0;
                }
              }
            }
            .hero-headings {
              .main-headline {
                margin-bottom: 0;
              }
              .main-headline span {
                font-size: 3.75rem;
              }
              .subheading {
                margin-bottom: 11.25rem;
              }
              .subheading span {
                font-size: 1.625rem;
              }
            }
            &--compact {
              .hero-headings {
                width: clamp(300px, 50vw, 369px);
                .main-headline {
                  margin-bottom: 1.875rem;
                  span {
                    font-size: 2.2rem;
                  }
                }
              }
              .two-columns .content {
                top: 2.5rem;
              }
            }
            &--with-gradient {
              background: linear-gradient(180deg, #dfeeff 4.45%, #ffffff 98.34%);
            }
          }
          @media (${mq.largePhone}) {
            .careers-hero-wrapper {
              .hero-headings {
                :global(.main-headline) {
                  margin-bottom: 0;
                }
                :global(.main-headline span) {
                  font-size: 3.75rem;
                }
                :global(.subheading) {
                  margin-bottom: 8.125rem;
                }
                :global(.subheading span) {
                  font-size: 1.625rem;
                }
              }
              .two-columns .content {
                position: absolute;
                top: 7.5rem;
                bottom: 0;
                grid-gap: 0;
              }
              &--compact {
                .hero-headings {
                  width: 100%;
                  .main-headline {
                    margin: 0 1.5rem 1.5rem;
                    span {
                      font-size: 2.2rem;
                    }
                  }
                }
                .two-columns .content {
                  top: 2.5rem;
                }
              }
            }
          }
          @media (${mq.mobile}) {
            .careers-hero-wrapper {
              &--has-content {
                :global(.hero-wrapper.two-columns) {
                  grid-template-columns: [content] 14fr;
                }
              }
            }
          }
          @media (${mq.tablet}) {
            .careers-hero-wrapper {
              position: relative;
              max-width: 1550px;
              margin: 0 auto;
              .headline span {
                font-family: 'Intercom Brush Large', ${fontGraphik};
              }
              min-height: ${layout.leftAlign ? '600px' : 'none'};
              display: ${layout.leftAlign ? 'flex' : 'block'};
              justify-content: center;
              .two-columns {
                &.hero-wrapper {
                  background: none;
                  height: auto;
                }
                .background-aux-content picture {
                  visibility: visible;
                }
                .content {
                  top: 3.75rem;
                }
                .headline {
                  max-width: 515px;
                  margin-bottom: 1.25rem;
                  span {
                    font-size: 8vw;
                  }
                }
              }
              .hero-headings {
                :global(.subheading) {
                  margin-bottom: 2.25rem;
                }
              }
              &--compact {
                .hero-headings {
                  width: 53%;
                  .main-headline {
                    margin-bottom: 2.375rem;
                    span {
                      font-size: clamp(2.2rem, 6vw, 4rem);
                    }
                  }
                }
                .two-columns .content {
                  top: 0;
                }
              }
            }
            :global(.careers-hero-wrapper .hero-wrapper.two-columns .hero) {
              grid-template-columns: [left-content] 7fr [right-content-sm] 1fr [right-content-lg] 5fr;
            }
          }
          @media (${mq.desktop}) {
            .careers-hero-wrapper {
              .hero-headings {
                :global(.main-headline span) {
                  font-size: 7.5rem;
                }
                :global(.subheading span) {
                  font-size: 2.25rem;
                }
              }
              &--compact {
                .hero-headings {
                  .main-headline span {
                    font-size: clamp(4rem, 6vw, 4.875rem);
                  }
                }
              }
            }
          }
          @media (${mq.desktopLg}) {
            .careers-hero-wrapper {
              .one-column .content .headline span,
              .two-columns .content .headline span {
                font-size: 6.25rem;
                line-height: 6.25rem;
              }
              .two-columns .headline {
                max-width: 500px;
                margin-bottom: 2.25rem;
              }
              .hero-headings {
                :global(.main-headline span) {
                  font-size: 9.375rem;
                }
              }
              .hero-wrapper.two-columns .hero {
                grid-template-columns: 1fr [left-content] 7fr [right-content-sm] 1fr [right-content-lg] 5fr 1fr;
              }
              &--compact {
                .hero-headings {
                  .main-headline span {
                    font-size: 4.875rem;
                  }
                }
                .hero-wrapper.two-columns .hero {
                  grid-template-columns: [left-content] 7fr [right-content-sm] 1fr [right-content-lg] 5fr;
                }
              }
            }
          }
        `}
      </style>
    </>
  )
}
